import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PaperPlaneTilt, ArrowsClockwise, DownloadSimple } from "@phosphor-icons/react";

import AdminLogin from "@/pages/admin/AdminLogin";
import FundraisingSummaryPanel from "@/pages/admin/FundraisingSummaryPanel";
import ParticipantsPanel from "@/pages/admin/ParticipantsPanel";
import FeaturedProductPicker from "@/pages/admin/FeaturedProductPicker";
import ProductImagesPanel from "@/pages/admin/ProductImagesPanel";
import MerchOrdersPanel from "@/pages/admin/MerchOrdersPanel";
import StatsGrid from "@/pages/admin/StatsGrid";
import TestimonialsPanel from "@/pages/admin/TestimonialsPanel";
import SocialCachePanel from "@/pages/admin/SocialCachePanel";
import LegacyWallPanel from "@/pages/admin/LegacyWallPanel";
import LoginAuditPanel from "@/pages/admin/LoginAuditPanel";
import TextBlastPanel from "@/pages/admin/TextBlastPanel";
import DailyReportPanel from "@/pages/admin/DailyReportPanel";
import CommunicationsPanel from "@/pages/admin/CommunicationsPanel";
import KmsRosterPanel from "@/pages/admin/KmsRosterPanel";
import LeadsTable from "@/pages/admin/LeadsTable";
import BundlesPanel from "@/pages/admin/BundlesPanel";
import ProductTagsPanel from "@/pages/admin/ProductTagsPanel";
import DownloadsResourcesPanel from "@/pages/admin/DownloadsResourcesPanel";
import MediaLibraryPanel from "@/pages/admin/MediaLibraryPanel";
import PaymentProviderPanel from "@/pages/admin/PaymentProviderPanel";
import AnalyticsPanel from "@/pages/admin/AnalyticsPanel";
import ExcludeDevicePanel from "@/pages/admin/ExcludeDevicePanel";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "sws_admin_token";
const TOKEN_EXPIRY_KEY = "sws_admin_token_expires";
// Admin sessions expire after 12h of inactivity — forces the password gate to
// come back on for anyone who logged in once and left the tab open.
const SESSION_TTL_MS = 12 * 60 * 60 * 1000;

function readValidToken() {
  try {
    const t = localStorage.getItem(TOKEN_KEY) || "";
    const exp = parseInt(localStorage.getItem(TOKEN_EXPIRY_KEY) || "0", 10);
    if (!t) return "";
    if (exp && Date.now() > exp) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      return "";
    }
    return t;
  } catch { return ""; }
}

export default function Admin() {
  const [token, setToken] = useState(() => readValidToken());
  const [leads, setLeads] = useState(null);
  const [stats, setStats] = useState(null);
  const [testimonials, setTestimonials] = useState(null);
  const [legacyPending, setLegacyPending] = useState({ entries: [], count: 0 });
  const [sacrificeSignups, setSacrificeSignups] = useState({ signups: [], total: 0, paid: 0, total_minutes: 0 });
  const [merchOrders, setMerchOrders] = useState({ orders: [], total: 0, paid: 0, printful_fulfilled: 0, printful_failed_count: 0, revenue: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reportStatus, setReportStatus] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(() => {
    try { return localStorage.getItem("sws_admin_show_advanced") === "1"; } catch { return false; }
  });
  const [showStore, setShowStore] = useState(() => {
    try { return localStorage.getItem("sws_admin_show_store") !== "0"; } catch { return true; }
  });
  const [showChallenge, setShowChallenge] = useState(() => {
    try { return localStorage.getItem("sws_admin_show_challenge") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("sws_admin_show_challenge", showChallenge ? "1" : "0"); } catch { /* ignore */ }
  }, [showChallenge]);

  useEffect(() => {
    try { localStorage.setItem("sws_admin_show_advanced", showAdvanced ? "1" : "0"); } catch { /* ignore */ }
  }, [showAdvanced]);
  useEffect(() => {
    try { localStorage.setItem("sws_admin_show_store", showStore ? "1" : "0"); } catch { /* ignore */ }
  }, [showStore]);

  const authed = !!token;

  // Scoped fetchers — one per admin panel so the per-section Refresh button
  // can reload just its slice of data without churning the whole dashboard.
  const authFetch = (path) =>
    fetch(`${BACKEND}${path}`, { headers: { "X-Admin-Token": token } });

  const handle401 = (r) => {
    if (r.status === 401) {
      try {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
      } catch { /* ignore */ }
      setToken("");
      setError("Unauthorized — check your admin token.");
      return true;
    }
    return false;
  };

  const fetchLeads = async () => {
    if (!token) return;
    const r = await authFetch(`/api/admin/leads`);
    if (handle401(r)) return;
    if (r.ok) setLeads(await r.json());
  };
  const fetchStats = async () => {
    if (!token) return;
    const r = await authFetch(`/api/admin/stats`);
    if (handle401(r)) return;
    if (r.ok) setStats(await r.json());
  };
  const fetchTestimonials = async () => {
    if (!token) return;
    const r = await authFetch(`/api/admin/testimonials`);
    if (handle401(r)) return;
    setTestimonials(r.ok ? await r.json() : { testimonials: [], pending: 0 });
  };
  const fetchLegacyPending = async () => {
    if (!token) return;
    const r = await authFetch(`/api/sacrifice/legacy-wall/pending`);
    if (handle401(r)) return;
    setLegacyPending(r.ok ? await r.json() : { entries: [], count: 0 });
  };
  const fetchSacrificeSignups = async () => {
    if (!token) return;
    const r = await authFetch(`/api/admin/sacrifice`);
    if (handle401(r)) return;
    setSacrificeSignups(r.ok ? await r.json() : { signups: [], total: 0, paid: 0, total_minutes: 0 });
  };
  const fetchMerchOrders = async () => {
    if (!token) return;
    const r = await authFetch(`/api/admin/merch-orders`);
    if (handle401(r)) return;
    setMerchOrders(r.ok ? await r.json() : { orders: [], total: 0, paid: 0, printful_fulfilled: 0, printful_failed_count: 0, revenue: 0 });
  };

  const fetchData = async () => {
    if (!token) return;
    setLoading(true); setError("");
    try {
      await Promise.all([
        fetchLeads(), fetchStats(), fetchTestimonials(),
        fetchLegacyPending(), fetchSacrificeSignups(), fetchMerchOrders(),
      ]);
    } catch (e) {
      setError("Failed to load. Backend reachable?");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (authed) fetchData(); }, [authed]);// eslint-disable-line react-hooks/exhaustive-deps

  const onLoginSuccess = (t) => {
    try {
      localStorage.setItem(TOKEN_KEY, t);
      localStorage.setItem(TOKEN_EXPIRY_KEY, String(Date.now() + SESSION_TTL_MS));
    } catch { /* private-mode ignore */ }
    setToken(t);
  };

  const onLogout = () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
    } catch { /* ignore */ }
    setToken(""); setLeads(null); setStats(null); setTestimonials(null);
    setLegacyPending({ entries: [], count: 0 });
    setSacrificeSignups({ signups: [], total: 0, paid: 0, total_minutes: 0 });
  };

  // Keep /admin OUT of search indexes + strip snippets/archives from
  // any accidental cache. Also swaps the tab title.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow,noarchive,nosnippet";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "Admin · Sweat With Slay";
    return () => {
      try { document.head.removeChild(meta); } catch { /* ignore */ }
      document.title = prevTitle;
    };
  }, []);

  const exportCsv = async () => {
    setReportStatus("preparing CSV…");
    try {
      const r = await fetch(`${BACKEND}/api/admin/leads.csv`, { headers: { "X-Admin-Token": token } });
      if (!r.ok) {
        setReportStatus(`CSV HTTP ${r.status}`);
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url; a.download = `sws-leads-${stamp}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setReportStatus(`CSV downloaded ✓`);
    } catch (e) {
      setReportStatus(`CSV failed: ${e?.message || "network"}`);
    }
    setTimeout(() => setReportStatus(""), 6000);
  };

  const triggerWeeklyReport = async () => {
    setReportStatus("sending…");
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 30000);
      const r = await fetch(`${BACKEND}/api/admin/weekly-report`, {
        method: "POST",
        headers: { "X-Admin-Token": token },
        signal: ctrl.signal,
      });
      clearTimeout(t);
      let data = {};
      try { data = await r.json(); } catch { /* may not be JSON on 5xx */ }
      if (r.ok) {
        setReportStatus(`sent (${data.count} leads) ✓`);
      } else {
        setReportStatus(`HTTP ${r.status}: ${data.detail || r.statusText || "failed"}`);
      }
    } catch (e) {
      const name = e?.name || "Error";
      const msg = name === "AbortError" ? "timed out after 30s" : (e?.message || "network");
      setReportStatus(`error: ${msg}`);
    }
    setTimeout(() => setReportStatus(""), 10000);
  };

  const runDiag = async () => {
    setReportStatus("running diag…");
    try {
      const r = await fetch(`${BACKEND}/api/admin/diag`, { headers: { "X-Admin-Token": token } });
      const d = await r.json();
      if (!r.ok) {
        setReportStatus(`diag HTTP ${r.status}`);
        return;
      }
      const parts = [
        `Resend key: ${d.has_resend_key ? `✓ ${d.resend_key_prefix}` : "✗ MISSING"}`,
        `From: ${d.sender_email || "—"}`,
        `To: ${d.owner_email || "—"}`,
        `Leads in DB: ${d.leads_total}`,
      ];
      setReportStatus(parts.join(" · "));
    } catch (e) {
      setReportStatus(`diag failed: ${e?.message || "network"}`);
    }
    setTimeout(() => setReportStatus(""), 15000);
  };

  const exportPrintfulPricingCsv = async () => {
    setReportStatus("building Printful pricing CSV…");
    try {
      const r = await fetch(`${BACKEND}/api/admin/products.csv`, { headers: { "X-Admin-Token": token } });
      if (!r.ok) {
        setReportStatus(`Printful CSV HTTP ${r.status}`);
        return;
      }
      const blob = await r.blob();
      const rowCount = r.headers.get("x-row-count") || "?";
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url; a.download = `sws-printful-pricing-${stamp}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setReportStatus(`Printful CSV downloaded ✓ (${rowCount} variants)`);
    } catch (e) {
      setReportStatus(`Printful CSV failed: ${e?.message || "network"}`);
    }
    setTimeout(() => setReportStatus(""), 15000);
  };

  const exportPrintfulSummaryCsv = async () => {
    setReportStatus("building product summary CSV…");
    try {
      const r = await fetch(`${BACKEND}/api/admin/products-summary.csv`, { headers: { "X-Admin-Token": token } });
      if (!r.ok) {
        setReportStatus(`Summary CSV HTTP ${r.status}`);
        return;
      }
      const blob = await r.blob();
      const rowCount = r.headers.get("x-row-count") || "?";
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url; a.download = `sws-printful-summary-${stamp}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setReportStatus(`Summary CSV downloaded ✓ (${rowCount} products)`);
    } catch (e) {
      setReportStatus(`Summary CSV failed: ${e?.message || "network"}`);
    }
    setTimeout(() => setReportStatus(""), 15000);
  };

  if (!authed) {
    return <AdminLogin onSuccess={onLoginSuccess} />;
  }

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0A] text-white font-body">
      <div className="border-b border-white/10 px-6 md:px-12 py-6 flex flex-wrap items-center justify-between gap-4 sticky top-0 bg-[#0A0A0A]/90 backdrop-blur-md z-10">
        <div className="flex items-center gap-4">
          <Link to="/" className="text-[11px] uppercase tracking-[0.3em] text-white/50 hover:text-[#FF1B91]">← Site</Link>
          <h1 className="font-display font-black uppercase text-xl tracking-tighter">Admin <span className="text-[#FF1B91]">·</span> Dashboard</h1>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="admin-refresh" onClick={fetchData} disabled={loading} className="text-[10px] uppercase tracking-[0.25em] text-white/60 hover:text-[#FF1B91] border border-white/15 px-3 py-2 inline-flex items-center gap-2">
            <ArrowsClockwise size={12} weight="bold" /> {loading ? "Loading…" : "Refresh"}
          </button>
          <button data-testid="admin-run-diag" onClick={runDiag} className="text-[10px] uppercase tracking-[0.25em] text-white/60 hover:text-[#FF1B91] border border-white/15 px-3 py-2">
            Diag
          </button>
          <button data-testid="admin-export-csv" onClick={exportCsv} className="text-[10px] uppercase tracking-[0.25em] text-white/80 hover:text-[#FF1B91] border border-white/15 px-3 py-2 inline-flex items-center gap-2">
            <DownloadSimple size={12} weight="bold" /> CSV
          </button>
          <button data-testid="admin-export-printful-pricing-csv" onClick={exportPrintfulPricingCsv} className="text-[10px] uppercase tracking-[0.25em] text-white border border-[#FF1B91]/60 bg-[#FF1B91]/5 hover:bg-[#FF1B91]/20 px-3 py-2 inline-flex items-center gap-2">
            <DownloadSimple size={12} weight="bold" /> Export Printful Pricing CSV
          </button>
          <button data-testid="admin-export-printful-summary-csv" onClick={exportPrintfulSummaryCsv} className="text-[10px] uppercase tracking-[0.25em] text-white border border-[#FF1B91]/60 bg-[#FF1B91]/5 hover:bg-[#FF1B91]/20 px-3 py-2 inline-flex items-center gap-2">
            <DownloadSimple size={12} weight="bold" /> Export Product Summary CSV
          </button>
          <button data-testid="admin-send-report" onClick={triggerWeeklyReport} className="text-[10px] uppercase tracking-[0.25em] text-white border border-[#FF1B91] bg-[#FF1B91]/10 hover:bg-[#FF1B91]/20 px-3 py-2 inline-flex items-center gap-2">
            <PaperPlaneTilt size={12} weight="bold" /> Send weekly report
          </button>
          {reportStatus && (
            <span data-testid="admin-status-line" className="text-[10px] uppercase tracking-[0.2em] text-[#FF1B91] border-l border-white/15 pl-3 max-w-md truncate">{reportStatus}</span>
          )}
          <button onClick={onLogout} className="text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-[#FF1B91]">Logout</button>
        </div>
      </div>

      <div className="px-6 md:px-12 py-10 pb-32 space-y-10">
        {error && <div className="border border-[#FF1B91] bg-[#FF1B91]/10 p-4 text-sm">{error}</div>}

        {/* ============================================================
            LEGACY CHALLENGE · YEAR 1 (2026) — ARCHIVED
            Year 1 is COMPLETE. Activity logging, registration and all
            automated emails are frozen via the backend CHALLENGE_CLOSED
            switch. Every record is intact and readable below. All of the
            manual challenge tools live under this one dropdown so they can
            be revived together when Year 2 opens (flip CHALLENGE_CLOSED to
            false on the backend). Collapsed by default. */}
        <div data-testid="admin-challenge-section">
          <button
            type="button"
            data-testid="admin-challenge-toggle"
            onClick={() => setShowChallenge((v) => !v)}
            className="w-full flex items-center justify-between px-5 py-4 border border-[#B32027]/50 hover:border-[#B32027] bg-[#B32027]/[0.06] text-left"
          >
            <div>
              <div className="font-display font-black uppercase text-base tracking-tight text-white/90">
                Legacy Challenge <span className="text-[#B32027]">·</span> Year 1 — 2026
                <span className="ml-3 text-[9px] uppercase tracking-[0.25em] text-emerald-300 border border-emerald-500/50 bg-emerald-500/10 px-2 py-1">Completed · frozen</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mt-1">
                Fundraising · Participants · Daily report · Communications · KMS roster · Login audit · Text blast · Legacy wall
              </div>
            </div>
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">
              {showChallenge ? "Hide ▲" : "Show ▼"}
            </span>
          </button>
          {showChallenge && (
            <div className="mt-4 space-y-10">
              <div className="border border-white/10 bg-[#0F0F0F] px-5 py-4 text-[12px] text-white/60 leading-relaxed">
                <span className="text-emerald-300 font-bold">Year 1 is archived.</span> Logging, registration and
                automated emails are turned off. Everything below still reads the final Year-1 records so you can review,
                and the manual <span className="text-white/80">Communications</span> panel can still send a one-off email.
                To run Year 2, set <code className="text-[#FF1B91]">CHALLENGE_CLOSED=false</code> in the backend env and redeploy.
              </div>

              {/* Challenge activity + fundraising at a glance (final numbers). */}
              <FundraisingSummaryPanel token={token} />

              {/* One participant view — final minutes, finishers, public names. */}
              <ParticipantsPanel token={token} />

              {/* Movement report (manual send only; auto-schedule is frozen). */}
              <DailyReportPanel token={token} />

              {/* Communications — manual reminders + updates. */}
              <CommunicationsPanel token={token} />

              {/* KMS team board roster. */}
              <KmsRosterPanel token={token} />

              {/* Login health + participant identifiers. */}
              <LoginAuditPanel token={token} />

              {/* Outreach — tap-to-text participants their log link. */}
              <TextBlastPanel token={token} />

              {/* Legacy Wall moderation + the Year-1 uploads. */}
              <LegacyWallPanel token={token} legacyPending={legacyPending} setLegacyPending={setLegacyPending} setError={setError} onRefresh={fetchLegacyPending} />
            </div>
          )}
        </div>

        {/* Core operational panels — merch store orders (not part of the
            frozen challenge; still live). */}
        <MerchOrdersPanel token={token} data={merchOrders} refresh={fetchMerchOrders} />

        {/* MERCH / STORE — every storefront-tuning knob consolidated behind
            one toggle so the top of the dashboard stays lean. */}
        <div data-testid="admin-store-section">
          <button
            type="button"
            data-testid="admin-store-toggle"
            onClick={() => setShowStore((v) => !v)}
            className="w-full flex items-center justify-between px-5 py-3 border border-white/15 hover:border-[#FF1B91] bg-[#0F0F0F] text-left"
          >
            <div>
              <div className="font-display font-black uppercase text-base tracking-tight text-white/90">
                Merch <span className="text-[#FF1B91]">·</span> Store
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mt-1">
                Featured drop · Product images · Payments · Bundles · Tags · Media library · Downloads
              </div>
            </div>
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">
              {showStore ? "Hide ▲" : "Show ▼"}
            </span>
          </button>
          {showStore && (
            <div className="mt-4 space-y-6">
              <FeaturedProductPicker token={token} />
              <ProductImagesPanel token={token} backend={BACKEND} />
              <PaymentProviderPanel token={token} />
              <BundlesPanel token={token} backend={BACKEND} />
              <ProductTagsPanel token={token} backend={BACKEND} />
              <MediaLibraryPanel />
              <DownloadsResourcesPanel />
            </div>
          )}
        </div>

        {/* ADVANCED — analytics, leads, testimonials, social cache, device
            exclude, lead stats. Hidden by default because most sessions
            don't need them. Persists your preference in localStorage. */}
        <div data-testid="admin-advanced-section">
          <button
            type="button"
            data-testid="admin-advanced-toggle"
            onClick={() => setShowAdvanced((v) => !v)}
            className="w-full flex items-center justify-between px-5 py-3 border border-white/10 hover:border-[#FF1B91] bg-[#0F0F0F] text-left"
          >
            <div>
              <div className="font-display font-black uppercase text-base tracking-tight text-white/70">
                Advanced <span className="text-white/25">·</span> Analytics &amp; Diagnostics
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mt-1">
                Traffic analytics · Leads · Testimonials · Social cache · Lead stats · Exclude device
              </div>
            </div>
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-bold">
              {showAdvanced ? "Hide ▲" : "Show ▼"}
            </span>
          </button>
          {showAdvanced && (
            <div className="mt-4 space-y-6">
              <AnalyticsPanel token={token} defaultOpen={false} />
              <LeadsTable leads={leads} onRefresh={fetchLeads} defaultOpen={false} />
              <TestimonialsPanel token={token} testimonials={testimonials} setTestimonials={setTestimonials} setError={setError} onRefresh={fetchTestimonials} />
              <SocialCachePanel token={token} setError={setError} />
              <StatsGrid stats={stats} onRefresh={fetchStats} />
              <ExcludeDevicePanel defaultOpen={false} />
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
